import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  ModalHeader,
  ModalFooter,
  HStack,
  Button,
  Text,
  Icon,
  Spinner,
  useToast,
  VStack,
  IconButton,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { FormProvider, useForm } from "react-hook-form";
import {
  createDialogContext,
  DialogProvider,
  useDialogWithOptions,
} from "src/Common/Dialog";
import { LookalikeIcon, SocialIcon } from "../AudienceIcons";
import { PageSelectionStep, AudienceTypeStep, LookalikeStep } from "./steps";
import {
  PageLookalikeAudienceFormValues,
  StepConfig,
  type StepProps,
} from "./types";
import { CloseIcon } from "@chakra-ui/icons";

export const PageLookalikeAudienceDialogContext = createDialogContext();

const PageLookalikeAudienceDialog = ({
  isOpen,
  close,
  onCreate = () => {},
  pages = [],
  instagramAccounts = [],
}: {
  onCreate?: (audience: any) => void;
  isOpen: boolean;
  close: () => void;
  pages?: Array<{
    id: string;
    name: string;
  }>;
  instagramAccounts?: Array<{
    id: string;
    username: string;
  }>;
}) => {
  const toast = useToast();
  const form = useForm<PageLookalikeAudienceFormValues>({
    defaultValues: {
      pageType: undefined,
      pageId: undefined,
      name: undefined,
      audienceMode: "engagers",
      createLookalike: false,
      lookalike: {
        name: "",
        ratio: "0-1",
      },
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const audienceMode = form.watch("audienceMode");

  // Step configuration
  const steps: StepConfig[] = [
    {
      id: 1,
      title: "Create audience from FB/IG",
      subtitle:
        "Select either a Facebook page or Instagram account to build an audience from",
      component: PageSelectionStep,
      nextButtonText: "Next",
      canProceed: (form) => form.watch("pageId"),
    },
    {
      id: 2,
      title: "Choose audience type",
      subtitle: "Create a page audience or a lookalike to reach similar people",
      component: AudienceTypeStep,
      nextButtonText:
        audienceMode === "lookalike" ? "Create Lookalike" : "Done",
      canProceed: (form) => !!form.watch("audienceMode"),
    },
    {
      id: 3,
      title: "Lookalike Audience Settings",
      subtitle: "Create a lookalike audience based on your IG/FB accounts",
      component: LookalikeStep,
      nextButtonText: "Done",
      canProceed: (form) => !!form.watch("lookalike.ratio"),
    },
  ];

  const currentStep = steps[currentStepIndex];

  const handleClose = useCallback(() => {
    form.reset();
    setCurrentStepIndex(0);
    close();
  }, [form, close]);

  const handleNext = useCallback(async () => {
    setIsLoading(true);
    try {
      setCurrentStepIndex((previousStepIndex) =>
        previousStepIndex < steps.length - 1
          ? previousStepIndex + 1
          : previousStepIndex
      );
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: "Unknown error",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [steps, toast]);

  const handleSubmit = useCallback(
    async (data: PageLookalikeAudienceFormValues) => {
      setIsLoading(true);
      try {
        onCreate(data);

        toast({
          title: "Success",
          description: "Audience created successfully",
          status: "success",
        });
        handleClose();
      } catch (error) {
        console.error("Error creating audience:", error);
        toast({
          title: "Error",
          description: "Failed to create audience",
          status: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [onCreate, toast, handleClose]
  );

  const handleSave = () => {
    // At the audience type step: "engagers" submits immediately, "lookalike" advances
    if (
      currentStepIndex === 1 &&
      form.getValues("audienceMode") !== "lookalike"
    ) {
      form.handleSubmit(handleSubmit)();
    } else if (currentStepIndex < steps.length - 1) {
      form.handleSubmit(handleNext)();
    } else {
      form.handleSubmit(handleSubmit)();
    }
  };

  const lookalikeOptions: StepProps["lookalikeOptions"] = [
    {
      title: "Create 0-1% lookalike",
      description: "~1M people • 24hr processing time",
      value: "0-1",
      icon: <SocialIcon />,
      iconBgColor: "#DFEAFD",
      isRecommended: true,
    },
    {
      title: "Create 0-3% lookalike",
      description: "this text for helper info about size, % range, purpose",
      value: "0-3",
      icon: <SocialIcon />,
      iconBgColor: "#FDF5DF",
      isRecommended: false,
    },
    {
      title: "Create 0-5% lookalike",
      description: "~5M people • 24hr processing time",
      value: "0-5",
      icon: <SocialIcon />,
      iconBgColor: "#FDE3DF",
      isRecommended: false,
    },
  ];

  const StepComponent = currentStep.component;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="white" borderRadius="lg" p={0}>
        <FormProvider {...form}>
          <Box px={6} id="page-lookalike-audience-form">
            <ModalHeader px={0}>
              <HStack justify="space-between">
                <Box>
                  <Text fontSize="lg" fontWeight="600">
                    {currentStep.title}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {currentStep.subtitle}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Close"
                  icon={<CloseIcon color="gray.500" fontSize={12} />}
                  onClick={handleClose}
                  variant="ghost"
                  color="gray.400"
                  size="xs"
                />
              </HStack>
            </ModalHeader>
            <StepComponent
              pages={pages}
              instagramAccounts={instagramAccounts}
              lookalikeOptions={lookalikeOptions}
            />
          </Box>
        </FormProvider>
        <ModalFooter>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Button
                  onClick={handleSave}
                  bgColor={
                    currentStep.nextButtonText === "Next" ? "gray.900" : "green"
                  }
                  color="white"
                  _hover={{
                    bgColor:
                      currentStep.nextButtonText === "Next"
                        ? "black"
                        : "green.600",
                  }}
                  isDisabled={!currentStep.canProceed(form)}
                  rounded="lg"
                  fontSize="16px"
                  fontWeight="semibold"
                  isLoading={isLoading}
                  rightIcon={
                    currentStep.nextButtonText === "Next" ? (
                      <Icon as={FiArrowRight} ml={2} />
                    ) : undefined
                  }
                >
                  {currentStep.nextButtonText}
                </Button>
              </HStack>
            </HStack>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const PageLookalikeAudienceDialogWrapper = ({
  onCreate,
  pages,
  instagramAccounts,
}: {
  onCreate: (audience: any) => void;
  pages?: Array<{
    id: string;
    name: string;
  }>;
  instagramAccounts?: Array<{
    id: string;
    username: string;
  }>;
}) => {
  const { isOpen, close } = useDialogWithOptions(
    PageLookalikeAudienceDialogContext
  );

  return (
    <PageLookalikeAudienceDialog
      onCreate={onCreate}
      isOpen={isOpen}
      close={close}
      pages={pages}
      instagramAccounts={instagramAccounts}
    />
  );
};

export const withPageLookalikeAudienceDialog =
  <P extends object>(Component: React.ComponentType<P>) =>
  (
    props: P & {
      onChange?: (audience: any) => void;
      pages?: Array<{
        id: string;
        name: string;
      }>;
      instagramAccounts?: Array<{
        id: string;
        username: string;
      }>;
    }
  ) => {
    return (
      <DialogProvider context={PageLookalikeAudienceDialogContext}>
        <Component {...props} />
        <PageLookalikeAudienceDialogWrapper
          onCreate={props.onChange || (() => {})}
          pages={props.pages}
          instagramAccounts={props.instagramAccounts}
        />
      </DialogProvider>
    );
  };

export default PageLookalikeAudienceDialog;
